import React, { Component } from 'react';
import './LookBook.css';
import { Image } from 'pixboost-react';
import { Input, Label } from 'reactstrap';
import pixboostConfig from '../../../config/pixboost';
import { breakPoints } from '../../../config/breakpoints';
import { setIn, removeIn, Map as ImmutableMap } from 'immutable';
import ScreenBreakPointSwitcher from './ScreenBreakPointSwitcher';
import AddSkuToolBar from './AddSkuToolBar';
import HotSpot from './HotSpot';
import { getRelativeCoordinates } from '../../../util/coordinates';

const getlastOrder = hotspots =>
  Object.entries(hotspots).reduce(
    (lastOrder, [, { order }]) => Math.max(order, lastOrder),
    0
  );

const getNewSku = hotspots => {
  const lastid = Object.entries(hotspots).reduce(
    (lastOrder, [sku]) =>
      Math.max(
        lastOrder,
        Number.parseInt(sku.replace(/^((new-)(\d+))|(.*)$/, '$3') || '0', 10)
      ),
    0
  );
  return `new-${lastid + 1}`;
};

class LookBook extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeScreen: 'lg',
      screens: {
        sm: breakPoints.sm,
        md: breakPoints.md,
        lg: breakPoints.lg
      },
      hotspots: this.props.hotspots || {},
      images: this.props.images || {}
    };

    this.imageRef = React.createRef();
    this.handleSkuChange = this.handleSkuChange.bind(this);
    this.removeHotspot = this.removeHotspot.bind(this);
    this.addHotspot = this.addHotspot.bind(this);
  }

  lookbookChanged() {
    const { hotspots, images } = this.state;
    this.props.onSave({
      hotspots,
      images
    });
  }

  startDraggingHotSpot({ hotSpot: activeHotSpot, sku }, evt) {
    evt.dataTransfer.dropEffect = 'move';
    this.setState({
      clickedAt: getRelativeCoordinates(evt.nativeEvent, evt.currentTarget),
      activeHotSpot,
      sku
    });
  }

  stopDraggingHotSpot() {
    this.setState({ where: null });
    if (this.state.where === 'over' || this.state.where === null) {
      return;
    }

    const { sku, activeScreen } = this.state.activeHotSpot;

    this.setState(
      {
        hotspots: removeIn(this.state.hotspots, [sku, activeScreen])
      },
      this.lookbookChanged
    );
  }

  draggingOverImageWrapper(evt) {
    this.setState({ where: 'over' });
    evt.dataTransfer.dropEffect = 'move';
    evt.preventDefault();
  }

  draggingOutImageWrapper(evt) {
    this.setState({ where: 'out' });
    evt.preventDefault();
  }

  dropToImageWrapper(evt) {
    evt.preventDefault();
    const {
      activeHotSpot: { sku, activeScreen },
      clickedAt
    } = this.state;
    const rect = this.imageRef.current.getBoundingClientRect();
    const { clientX, clientY } = evt;
    const { left, top } = rect;
    const x = `${((clientX - left - clickedAt.x) / rect.width) * 100}%`;
    const y = `${((clientY - top - clickedAt.y) / rect.height) * 100}%`;

    this.setState(
      {
        hotspots: setIn(this.state.hotspots, [sku, activeScreen], { x, y }),
        where: null
      },
      this.lookbookChanged
    );
  }

  handleImageChange(e) {
    const { value } = e.target;
    const { activeScreen } = this.state;

    this.setState(
      {
        images: setIn(this.state.images, [activeScreen], value)
      },
      this.lookbookChanged
    );
  }

  handleSkuChange({ sku, value }) {
    let hotSpotMap = ImmutableMap(this.state.hotspots);
    hotSpotMap = hotSpotMap.set(value, hotSpotMap.get(sku)).delete(sku);
    this.setState({ hotspots: hotSpotMap.toObject() }, this.lookbookChanged);
  }

  removeHotspot(sku) {
    this.setState(
      {
        hotspots: removeIn(this.state.hotspots, [sku])
      },
      this.lookbookChanged
    );
  }

  addHotspot() {
    const sku = getNewSku(this.state.hotspots);
    const order = getlastOrder(this.state.hotspots) + 1;
    this.setState(
      {
        hotspots: setIn(this.state.hotspots, [sku], { order })
      },
      this.lookbookChanged
    );
  }

  render() {
    const { imageBasePath, title, className } = this.props;

    const { activeScreen, screens, hotspots, images, where } = this.state;

    const createDraggableProps = ({ sku, activeScreen }) => ({
      draggable: true,
      onDragStart: evt =>
        this.startDraggingHotSpot({ hotSpot: { activeScreen, sku } }, evt),
      onDragEnd: evt => this.stopDraggingHotSpot(evt)
    });

    return (
      <div className={className}>
        <div className="navigation">
          <ScreenBreakPointSwitcher
            currentBreakPoint={activeScreen}
            onBreakPointSelect={selectedBreakPoint =>
              this.setState({ activeScreen: selectedBreakPoint })
            }
          />
          <div className="form">
            <Label for="image">{this.props.imageBasePath}</Label>
            <Input
              id="image"
              onChange={e => this.handleImageChange(e)}
              value={images[activeScreen] || ''}
            />
          </div>
        </div>
        <div className={'frame-wrapper'}>
          {Object.keys(images).length > 0 && (
            <div
              className={`frame ${where ? where : ''}`}
              style={{ width: screens[activeScreen] }}
              ref={this.imageRef}
              onDragOver={evt => this.draggingOverImageWrapper(evt)}
              onDragLeave={evt => this.draggingOutImageWrapper(evt)}
              onDrop={evt => this.dropToImageWrapper(evt)}
            >
              {images[activeScreen] && (
                <Image
                  src={imageBasePath + images[activeScreen]}
                  alt={title}
                  config={pixboostConfig}
                  lazy={false}
                />
              )}

              {Object.entries(hotspots)
                .filter(([, hotspot]) => hotspot[activeScreen])
                .map(([sku, hotspot]) => (
                  <HotSpot
                    key={sku}
                    sku={sku}
                    {...hotspot[activeScreen]}
                    {...createDraggableProps({ activeScreen, sku })}
                    onChange={this.handleSkuChange}
                  />
                ))}
            </div>
          )}
        </div>

        <AddSkuToolBar
          hotspots={Object.entries(hotspots).map(([sku, hotspot]) => ({
            sku,
            isActive: hotspot[activeScreen] !== undefined,
            order: hotspot.order
          }))}
          onDragSkuStart={({ evt, sku }) =>
            this.startDraggingHotSpot({ hotSpot: { activeScreen, sku } }, evt)
          }
          onDragEnd={evt => this.stopDraggingHotSpot(evt)}
          onChange={this.handleSkuChange}
          removeHotspot={this.removeHotspot}
          addHotspot={this.addHotspot}
        />
      </div>
    );
  }
}

export default LookBook;



// WEBPACK FOOTER //
// ./src/components/library/LookBook/LookBook.js